import React, { useRef, useContext, useState, useEffect } from 'react';
// Import useSpring
import { motion, useScroll, useTransform, useSpring, AnimatePresence } from 'framer-motion';
import './HeroSection.css';
import videoBackground from '../7cfKwwJbdvTH5qlQZF3Yfixo.mp4';
import { ScrollContext } from './ScrollContext';
import Counter from './Counter'; // Make sure to import the Counter component
import Health from './CardContent/SZAPortal/Health.png';
import Omics from './CardContent/SZAPortal/Omics.png';
import AI from './CardContent/SZAPortal/AI.png';

import NewGenome from './CardContent/SZAPortal/NewGenome.png';
import OncoRisk from './CardContent/SZAPortal/OncoRisk.png';


// HELPER COMPONENT 1: Word (No changes)
const Word = ({ children, progress, range }) => {
  const opacity = useTransform(progress, range, [0.1, 1]);
  return (
    <motion.span className="ULTHOME-word-wrapper" style={{ opacity }}>
      {children}
    </motion.span>
  );
};

// HELPER COMPONENT 2: AnimatedText (UPDATED to have its own slow spring)
const AnimatedText = ({ lines, className }) => {
  const containerRef = useRef(null);
  const scrollContainerRef = useContext(ScrollContext);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "center center"],
    container: scrollContainerRef,
  });

  // ✅ 1. CREATE A SLOW SPRING SPECIFICALLY FOR THE TEXT
  // Lower stiffness and damping makes it feel "heavier" and slower.
  const slowSpringConfig = { stiffness: 65, damping: 25, restDelta: 0.001 };
  const smoothTextProgress = useSpring(scrollYProgress, slowSpringConfig);

  const allWords = lines.flatMap(line => line.split(' '));
  const totalWords = allWords.length;
  let wordCounter = 0;

  return (
    <h2 ref={containerRef} className={className}>
      {lines.map((line, lineIndex) => (
        <React.Fragment key={lineIndex}>
          {line.split(" ").map((word, wordIndexInLine) => {
            const start = wordCounter / totalWords;
            const end = (wordCounter + 1) / totalWords;
            wordCounter++;
            return (
              <React.Fragment key={wordIndexInLine}>
                {/* 2. Pass the new 'smoothTextProgress' to the Word component */}
                <Word progress={smoothTextProgress} range={[start, end]}>{word}</Word>
                {' '}
              </React.Fragment>
            );
          })}
          {lineIndex < lines.length - 1 && <br />}
        </React.Fragment>
      ))}
    </h2>
  );
};


// YOUR MAIN HERO SECTION COMPONENT (UPDATED)
const HeroSection = () => {
  const sectionRef = useRef(null);
  const scrollContainerRef = useContext(ScrollContext);

  // Image cycling state
  const images = [Health, Omics, AI, NewGenome, OncoRisk];
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Cycle through images every 2 seconds with buttery smooth transition
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % images.length);
    }, 2000);

    return () => clearInterval(interval);
  }, [images.length]);

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end end"],
    container: scrollContainerRef,
  });

  // ✅ 3. CREATE A SNAPPY SPRING FOR THE PARALLAX EFFECTS
  const parallaxSpringConfig = { stiffness: 100, damping: 30, restDelta: 0.001 };
  const smoothParallaxProgress = useSpring(scrollYProgress, parallaxSpringConfig);

  // --- 4. DRIVE ALL TRANSFORMS FROM THE 'smoothParallaxProgress' VALUE ---
  const taglineY = useTransform(smoothParallaxProgress, [0, 1], [0, -100]);
  const mainContentY = useTransform(smoothParallaxProgress, [0, 1], [0, -150]);
  const cardY = useTransform(smoothParallaxProgress, [0, 1], [0, -200]);
  const sideTextY = useTransform(smoothParallaxProgress, [0, 1], [0, -80]);
  const secondSectionY = useTransform(smoothParallaxProgress, [0, 1], [0, -250]);
  
  // ✅ SCROLL-BASED ANIMATIONS - SLIDE UP AND GET CUT OFF (EARLY & GENTLE)
  const slideUpY = useTransform(smoothParallaxProgress, [0.1, 0.8], [0, -120]);
  const slideUpOpacity = useTransform(smoothParallaxProgress, [0, 0.15, 0.5, 0.8], [1, 0.9, 0.4, 0]);
  const titleSlideY = useTransform(smoothParallaxProgress, [0.1, 0.7], [0, -100]);
  const ctaSlideY = useTransform(smoothParallaxProgress, [0.15, 0.8], [0, -90]);
  const statsSlideY = useTransform(smoothParallaxProgress, [0.2, 0.9], [0, -80]);
  
  const fireballBottom1 = useTransform(smoothParallaxProgress, [0, 1], ["5vh", "60vh"]);
  const fireballBottom2 = useTransform(smoothParallaxProgress, [0, 1], ["0vh", "24vh"]);
  const fireballBottom3 = useTransform(smoothParallaxProgress, [0, 1], ["0vh", "69vh"]);
  const fireballBottom4 = useTransform(smoothParallaxProgress, [0, 1], ["5vh", "32vh"]);

  const secondTitleLines = [
    "Powerful platforms",
    "for the collection, analysis,",
    "research and delivery",
    "of omics data."
  ];

  return (
    <section className="ULTHOME-hero-section" ref={sectionRef}>
      <motion.video 
        className="ULTHOME-hero-video"
        autoPlay muted loop playsInline
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ 
          duration: 1.5,
          ease: [0.25, 0.46, 0.45, 0.94]
        }}
      >
        <source src={videoBackground} type="video/mp4" />
      </motion.video>
      <motion.div 
        className="ULTHOME-video-overlay"
        initial={{ opacity: 1 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 0.2 }}
      ></motion.div>

      {/* --- FIRST SECTION --- */}
      <div className="ULTHOME-content-section ULTHOME-section-1">
        <div className="ULTHOME-hero-grid">

          <motion.div 
            className="ULTHOME-main-content"
            style={{ 
              y: mainContentY,
              opacity: slideUpOpacity
            }}
            initial={{ opacity: 0, y: 100 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.4 }}
          >
<motion.h1 
  className="ULTHOME-title"
  style={{ 
    y: titleSlideY,
    opacity: slideUpOpacity
  }}
>
  <div className='ULTHOME-Line'> </div>
  A gateway<br />
  to multi-omics <span className="ULTHOME-highlight">research</span><br />
  and <span className="ULTHOME-highlight">discovery</span> at scale.
</motion.h1>


            <motion.div 
              className="ULTHOME-cta-section-cover"
              style={{ 
                y: ctaSlideY,
                opacity: slideUpOpacity
              }}
            >

            <div className="ULTHOME-cta-section">
              <div className="ULTHOME-cta-text">
                <h3>See Platform in action</h3>
                <p>Join our guided tour and explore<br />all features live.</p>
              </div>
            </div>
            <div className='ULTHOME-Line1'> </div>

            <button className="ULTHOME-demo-btn">Get In Contact</button>

            </motion.div>



            <motion.div 
              className="ULTHOME-stats-row"
              style={{ 
                y: statsSlideY,
                opacity: slideUpOpacity
              }}
            >
              <div className="ULTHOME-stat">
                <span className="ULTHOME-stat-number">  <Counter 
    from={18}       // Start at 12,000
    to={25}         // Rush to 16,500 over 3 seconds
    duration={3}       
    slowIncrement={7} // Then, add 10 (from 16,500 to 16,510)
    slowDuration={3}   // ...over the next 7 seconds
  /><span className="ULTHOME-percent"></span></span>
                <div className="ULTHOME-stat-info">
                  <span className="ULTHOME-stat-label">Bioinformatic Tool Integrations</span>
                  <span className="ULTHOME-stat-sublabel">In-House and Growing</span>
                </div>
              </div>
              <div className="ULTHOME-stat">
              <span className="ULTHOME-stat-number">
  <Counter 
    from={12000}       // Start at 12,000
    to={16502}         // Rush to 16,500 over 3 seconds
    duration={3}       
    slowIncrement={10} // Then, add 10 (from 16,500 to 16,510)
    slowDuration={7}   // ...over the next 7 seconds
  />
  <span className="ULTHOME-percent"></span>
</span>                <div className="ULTHOME-stat-info">
                  <span className="ULTHOME-stat-label">Samples Processed</span>
                  <span className="ULTHOME-stat-sublabel">Across Platforms</span>
                </div>
              </div>
            </motion.div>
          </motion.div>
          <motion.div 
            className="ULTHOME-neural-card"
            style={{ 
              y: slideUpY,
              opacity: slideUpOpacity
            }}
            initial={{ opacity: 0, x: 100 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1, delay: 1 }}
          >
            <div className="ULTHOME-card-visual" style={{ overflow: 'visible' }}>
              {images.map((image, index) => (
                <motion.img 
                  key={index}
                  src={image} 
                  alt={`Image ${index + 1}`}
                  style={{
                    position: 'absolute',
        
                    height: '100%',
                    width: 'auto',
                    objectFit: 'contain'
                  }}
                  initial={false}
                  animate={{ 
                    opacity: currentImageIndex === index ? 1 : 0,
                    scale: currentImageIndex === index ? 1 : 1.05
                  }}
                  transition={{ 
                    duration: 0.8,
                    ease: [0.25, 0.46, 0.45, 0.94], // Custom easing for smoothness
                    opacity: { duration: 0.6 },
                    scale: { duration: 1.2 }
                  }}
                />
              ))}
            </div>
          </motion.div>

        </div>
      </div>
      
      {/* --- SECOND SECTION --- */}
      <motion.div 
        className="ULTHOME-content-section ULTHOME-section-2"
        style={{ y: secondSectionY }}
      >
        <div className="ULTHOME-second-grid">
          <motion.div className="ULTHOME-fireball-bg-1" style={{ bottom: fireballBottom1 }} />
          <motion.div className="ULTHOME-fireball-bg-2" style={{ bottom: fireballBottom2 }} />
          <motion.div className="ULTHOME-fireball-bg-3" style={{ bottom: fireballBottom3 }} />
          
          <div className="ULTHOME-second-content">
            <AnimatedText 
              lines={secondTitleLines} 
              className="ULTHOME-second-title" 
            />
          </div>
          
          <motion.div className="ULTHOME-orange-shape" style={{ bottom: fireballBottom4 }}></motion.div>
        </div>
      </motion.div>
    </section>
  );
};

export default HeroSection;